import { useLocalStorage } from "@vueuse/core";
import type { Ref } from "vue";
import useCurrentLocation from "~/composables/useCurrentLocation";
import type { User } from "~/types/oauth";
import type { OrganizationIdentifier } from "~/types/api";

export type { OrganizationIdentifier } from "~/types/api";

export function resolveCurrentOrganizationId(
  organizations: User["organizations"] | undefined,
  selectedOrganizationId: OrganizationIdentifier | null | undefined,
): OrganizationIdentifier | null {
  if (!organizations?.length) {
    return null;
  }

  if (selectedOrganizationId && organizations.some((organization) => organization.id === selectedOrganizationId)) {
    return selectedOrganizationId;
  }

  const onlyOrganization = organizations[0];
  if (organizations.length === 1 && onlyOrganization) {
    return onlyOrganization.id;
  }

  return null;
}

export function createCurrentOrganizationState(
  currentOrganizationId: Ref<OrganizationIdentifier | null>,
  forgetLocation: () => Promise<void>,
) {
  const syncCurrentOrganization = (user: Pick<User, "organizations"> | null | undefined): void => {
    currentOrganizationId.value = resolveCurrentOrganizationId(user?.organizations, currentOrganizationId.value);
  };

  const forgetOrganization = async (): Promise<void> => {
    await forgetLocation();
    currentOrganizationId.value = null;
  };

  const clearOrganization = (): void => {
    currentOrganizationId.value = null;
  };

  const switchOrganization = async (organizationId: OrganizationIdentifier): Promise<void> => {
    if (currentOrganizationId.value !== organizationId) {
      await forgetLocation();
      currentOrganizationId.value = organizationId;
    }
  };

  return {
    currentOrganizationId,
    syncCurrentOrganization,
    switchOrganization,
    forgetOrganization,
    clearOrganization,
  };
}

export default () => {
  const currentOrganizationId = useLocalStorage<OrganizationIdentifier | null>("api_organization_id", null);
  const { forgetLocation } = useCurrentLocation();

  return createCurrentOrganizationState(currentOrganizationId, forgetLocation);
};
